import Axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { showFailToast, showToast } from 'vant';

import NProgress from '../progress';

import { ContentTypeEnum } from '@/enums/request-enum';
import { usePortalLoginStore } from '@/store/user';
import 'vant/es/toast/style';

// 默认 axios 实例请求配置
const configDefault = {
  headers: {
    'Content-Type': ContentTypeEnum.JSON,
  },
  timeout: 0,
  baseURL: import.meta.env.VITE_BASE_API,
  data: {},
};

class Http {
  // 当前实例
  private static axiosInstance: AxiosInstance;
  // 请求配置
  private static axiosConfigDefault: AxiosRequestConfig;

  // 请求拦截
  private httpInterceptorsRequest(): void {
    Http.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        NProgress.start();
        // 发送请求前，可在此携带 token
        const token = usePortalLoginStore().portalLogin?.token;
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        // 开发/测试环境下打印请求信息
        if (import.meta.env.MODE !== 'prod') {
          console.group(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
          console.log('📍 Base URL:', config.baseURL);
          console.log('🔗 Full URL:', `${config.baseURL}${config.url}`);
          console.log('📋 Headers:', config.headers);
          if (config.params && Object.keys(config.params).length > 0) {
            console.log('🔍 Params:', config.params);
          }
          if (config.data) {
            console.log('📦 Data:', config.data);
          }
          console.log('⏱️ Timeout:', config.timeout);
          console.groupEnd();
        }
        // 开发/测试环境下记录请求开始时间，用于计算耗时
        if (import.meta.env.MODE !== 'prod') {
          (config as any).requestStartTime = Date.now();
        }
        return config;
      },
      (error: AxiosError) => {
        showFailToast(error.message);
        return Promise.reject(error);
      },
    );
  }

  // 响应拦截
  private httpInterceptorsResponse(): void {
    Http.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        NProgress.done();
        // 开发/测试环境下打印响应信息
        if (import.meta.env.MODE !== 'prod') {
          const { config, status, statusText, data, headers } = response;
          console.group(`✅ API Response: ${config.method?.toUpperCase()} ${config.url}`);
          console.log('📊 Status Code:', status);
          console.log('📝 Status Text:', statusText);
          console.log('🔗 Request URL:', `${config.baseURL}${config.url}`);
          console.log('📋 Response Headers:', headers);
          console.log('📦 Response Data:', data);
          console.log('📏 Content Length:', headers['content-length'] || 'N/A');
          console.log('🕐 Server Date:', headers['date'] || 'N/A');
          console.log('🔄 Response Type:', response.request?.responseType || 'json');
          console.log('⏱️ Duration:', `${Date.now() - (config as any).requestStartTime || 0}ms`);

          // 如果是业务响应，打印业务状态码
          if (data && typeof data === 'object' && data.code !== undefined) {
            console.log('🏢 Business Code:', data.code);
            console.log('💬 Business Message:', data.message || data.msg || 'N/A');
          }
          console.groupEnd();
        }

        // 如果是 Blob 类型响应，直接返回 data，不进行 JSON 解析
        if (response.config.responseType === 'blob') {
          // 检查是否真的是 blob（防止后端返回 JSON 错误）
          if (response.data instanceof Blob) {
            return response.data;
          }
        }

        // 与后端协定的返回字段
        const { code, data, message } = response.data;
        const silentError = !!(response.config as any).silentError;
        if (code >= 200 && code < 300) {
          return data;
        } else if (code === 401) {
          // 401 未授权，发送通知用户重新登录（静默请求不打断页，如免登录 click）
          if (!silentError) {
            window.dispatchEvent(new CustomEvent('unauthorized'));
          }
          return Promise.reject({ code, message });
        } else {
          // 处理请求错误
          if (!silentError) {
            showToast(message);
          }
          return Promise.reject({ code, message });
        }
      },
      (error: AxiosError) => {
        NProgress.done();
        // 处理 HTTP 网络错误
        let message = '';
        // HTTP 状态码
        const status = error.response?.status;
        switch (status) {
          case 400:
            message = '请求错误';
            break;
          case 401:
            message = '未授权，请登录';
            break;
          case 403:
            message = '拒绝访问';
            break;
          case 404:
            message = `请求地址出错: ${error.response?.config?.url}`;
            break;
          case 408:
            message = '请求超时';
            break;
          case 500:
            message = '服务器内部错误';
            break;
          case 501:
            message = '服务未实现';
            break;
          case 502:
            message = '网关错误';
            break;
          case 503:
            message = '服务不可用';
            break;
          case 504:
            message = '网关超时';
            break;
          case 505:
            message = 'HTTP版本不受支持';
            break;
          default:
            message = '网络连接故障';
        }

        const silentError = !!(error.config as any)?.silentError;
        if (!silentError) {
          showFailToast(message);
        }
        // 开发/测试环境下打印错误信息
        if (import.meta.env.MODE !== 'prod') {
          console.group('❌ API Error');

          if (error.config) {
            console.log('🔗 Request URL:', `${error.config.baseURL}${error.config.url}`);
            console.log('� Request Method:', error.config.method?.toUpperCase());
            console.log('�📋 Request Headers:', error.config.headers);
            if (error.config.data) {
              console.log('📦 Request Data:', error.config.data);
            }
            if (error.config.params && Object.keys(error.config.params).length > 0) {
              console.log('🔍 Request Params:', error.config.params);
            }
          }

          if (error.response) {
            console.log('📊 Response Status:', error.response.status);
            console.log('� Response Status Text:', error.response.statusText);
            console.log('�📋 Response Headers:', error.response.headers);
            console.log('📦 Response Data:', error.response.data);
            console.log('📏 Content Length:', error.response.headers['content-length'] || 'N/A');

            // 如果是业务错误响应，打印业务状态码
            if (error.response.data && typeof error.response.data === 'object') {
              if (error.code !== undefined) {
                console.log('🏢 Business Code:', error.code);
              }
              if (error.message || error.message) {
                console.log('💬 Business Message:', error.message);
              }
            }
          } else if (error.request) {
            console.log('📡 No Response Received');
            console.log('🔗 Request Info:', error.request);
          }

          console.log('❗ Error Type:', error.name || 'Unknown');
          console.log('💭 Error Message:', error.message);
          console.log('⏱️ Request Duration:', error.config ? `${Date.now() - (error.config as any).requestStartTime || 0}ms` : 'N/A');
          console.log('🔍 Full Error Object:', error);
          console.groupEnd();
        }
        return Promise.reject(error);
      },
    );
  }

  constructor(config: AxiosRequestConfig) {
    Http.axiosConfigDefault = config;
    Http.axiosInstance = Axios.create(config);
    this.httpInterceptorsRequest();
    this.httpInterceptorsResponse();
  }

  // 通用请求函数
  public request<T>(paramConfig: AxiosRequestConfig): Promise<T> {
    const config = { ...Http.axiosConfigDefault, ...paramConfig };
    return new Promise((resolve, reject) => {
      Http.axiosInstance
        .request(config)
        .then((response: any) => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }
}

export const http = new Http(configDefault);
